/**
 * Reporting Core SDK - React Hooks
 *
 * Headless React hooks for integrating the Reporting API.
 * These hooks do not render UI - they expose state and actions
 * for host applications to build their own UI.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createReportingClient, ReportingClient } from './client';
import {
  FilterOptionsCache,
  globalOptionsCache,
  initializeFilterValues,
  isQueryBackedFilter,
  prepareFiltersForSubmission,
  validateFilterValues
} from './filters';
import type {
  AsyncState,
  AuthProviderConfig,
  ExportMetadata,
  Filter,
  FilterOption,
  FilterValues,
  HtmlPaginationData,
  HtmlReportResult,
  Report,
  ReportConfig,
  ReportData,
  ReportingClientConfig,
  ReportRunMetadata
} from './types';
import { ReportingApiError } from './types';

// ============================================================================
// Context Types
// ============================================================================

/**
 * SDK context value for provider pattern.
 */
export interface ReportingContextValue {
  client: ReportingClient;
  optionsCache: FilterOptionsCache;
}

// ============================================================================
// useReports Hook
// ============================================================================

/**
 * State and actions for the useReports hook.
 */
export interface UseReportsResult extends AsyncState<Report[]> {
  /** Refresh the reports list */
  refresh: () => Promise<void>;
}

/**
 * Hook for listing available reports.
 *
 * @example
 * ```tsx
 * const { data: reports, loading, error, refresh } = useReports({
 *   baseUrl: process.env.REACT_APP_REPORTING_API_BASE_URL,
 *   authProvider: { getAccessToken: () => token },
 * });
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return (
 *   <ul>
 *     {reports?.map(r => <li key={r.id}>{r.name}</li>)}
 *   </ul>
 * );
 * ```
 */
export function useReports(config: ReportingClientConfig): UseReportsResult {
  const [state, setState] = useState<AsyncState<Report[]>>({
    data: null,
    loading: true,
    error: null
  });

  const clientRef = useRef<ReportingClient | null>(null);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  const fetchReports = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const result = await clientRef.current!.listReports();
      setState({ data: result.reports, loading: false, error: null });
    } catch (err) {
      const error =
        err instanceof ReportingApiError
          ? err
          : new ReportingApiError(
              err instanceof Error ? err.message : 'Unknown error'
            );
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return {
    ...state,
    refresh: fetchReports
  };
}

// ============================================================================
// useReportConfig Hook
// ============================================================================

/**
 * State and actions for the useReportConfig hook.
 */
export interface UseReportConfigResult extends AsyncState<ReportConfig> {
  /** Refresh the config */
  refresh: () => Promise<void>;
}

/**
 * Hook for loading report configuration.
 *
 * @param config - SDK client config
 * @param reportId - Report ID to load config for (null to skip)
 */
export function useReportConfig(
  config: ReportingClientConfig,
  reportId: string | null
): UseReportConfigResult {
  const [state, setState] = useState<AsyncState<ReportConfig>>({
    data: null,
    loading: false,
    error: null
  });

  const clientRef = useRef<ReportingClient | null>(null);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  const fetchConfig = useCallback(async () => {
    if (!reportId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const result = await clientRef.current!.getReportConfig(reportId);
      setState({ data: result, loading: false, error: null });
    } catch (err) {
      const error =
        err instanceof ReportingApiError
          ? err
          : new ReportingApiError(
              err instanceof Error ? err.message : 'Unknown error'
            );
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [reportId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    ...state,
    refresh: fetchConfig
  };
}

// ============================================================================
// useFilterState Hook
// ============================================================================

/**
 * State and actions for filter management.
 */
export interface UseFilterStateResult {
  /** Current filter values */
  values: FilterValues;

  /** Set a single filter value */
  setValue: (filterId: string, value: unknown) => void;

  /** Set multiple filter values */
  setValues: (updates: FilterValues) => void;

  /** Reset to initial defaults */
  reset: () => void;

  /** Validate current values (throws on error) */
  validate: () => void;

  /** Get prepared values for submission (throws on error) */
  getSubmitValues: () => FilterValues;
}

/**
 * Hook for managing filter state.
 *
 * Initializes from config defaults and provides validation.
 */
export function useFilterState(
  config: ReportConfig | null
): UseFilterStateResult {
  const [values, setValues] = useState<FilterValues>({});

  // Initialize values when config changes
  useEffect(() => {
    if (config) {
      setValues(initializeFilterValues(config));
    } else {
      setValues({});
    }
  }, [config]);

  const setValue = useCallback((filterId: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [filterId]: value }));
  }, []);

  const setMultipleValues = useCallback((updates: FilterValues) => {
    setValues((prev) => ({ ...prev, ...updates }));
  }, []);

  const reset = useCallback(() => {
    if (config) {
      setValues(initializeFilterValues(config));
    } else {
      setValues({});
    }
  }, [config]);

  const validate = useCallback(() => {
    if (config) {
      validateFilterValues(config.filters, values);
    }
  }, [config, values]);

  const getSubmitValues = useCallback(() => {
    if (config) {
      validateFilterValues(config.filters, values);
    }
    return prepareFiltersForSubmission(values);
  }, [config, values]);

  return {
    values,
    setValue,
    setValues: setMultipleValues,
    reset,
    validate,
    getSubmitValues
  };
}

// ============================================================================
// useFilterOptions Hook
// ============================================================================

/**
 * State for a single filter's options.
 */
export interface FilterOptionsState {
  options: FilterOption[];
  loading: boolean;
  error: ReportingApiError | null;
}

/**
 * Result from useFilterOptions hook.
 */
export interface UseFilterOptionsResult {
  /** Options state by filter ID */
  optionsState: Record<string, FilterOptionsState>;

  /** Fetch options for a filter */
  fetchOptions: (
    filterId: string,
    dependencyValues?: FilterValues
  ) => Promise<void>;

  /** Get options for a filter (from cache or state) */
  getOptions: (filterId: string) => FilterOption[];

  /** Check if a filter is loading */
  isLoading: (filterId: string) => boolean;

  /** Get error for a filter */
  getError: (filterId: string) => ReportingApiError | null;
}

/**
 * Hook for loading filter options with cascading support.
 *
 * @param config - SDK client config
 * @param reportId - Report ID
 * @param filters - Filter definitions
 * @param filterValues - Current filter values (for cascading)
 */
export function useFilterOptions(
  config: ReportingClientConfig,
  reportId: string | null,
  filters: Filter[],
  filterValues: FilterValues
): UseFilterOptionsResult {
  const [optionsState, setOptionsState] = useState<
    Record<string, FilterOptionsState>
  >({});

  const clientRef = useRef<ReportingClient | null>(null);
  const cacheRef = useRef(globalOptionsCache);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  // Get query-backed filters
  const queryFilters = useMemo(() => filters.filter(isQueryBackedFilter), [
    filters
  ]);

  const fetchOptions = useCallback(
    async (filterId: string, dependencyValues?: FilterValues) => {
      if (!reportId) return;

      const cacheKey = FilterOptionsCache.createKey(
        reportId,
        filterId,
        dependencyValues
      );
      const cached = cacheRef.current.get(cacheKey);

      if (cached) {
        setOptionsState((prev) => ({
          ...prev,
          [filterId]: { options: cached, loading: false, error: null }
        }));
        return;
      }

      setOptionsState((prev) => ({
        ...prev,
        [filterId]: {
          options: prev[filterId]?.options ?? [],
          loading: true,
          error: null
        }
      }));

      try {
        const result = await clientRef.current!.getFilterOptions(
          reportId,
          filterId,
          dependencyValues
        );

        cacheRef.current.set(cacheKey, result.options);

        setOptionsState((prev) => ({
          ...prev,
          [filterId]: { options: result.options, loading: false, error: null }
        }));
      } catch (err) {
        const error =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(
                err instanceof Error ? err.message : 'Unknown error'
              );

        setOptionsState((prev) => ({
          ...prev,
          [filterId]: {
            options: prev[filterId]?.options ?? [],
            loading: false,
            error
          }
        }));
      }
    },
    [reportId]
  );

  // Auto-fetch options for query-backed filters
  useEffect(() => {
    for (const filter of queryFilters) {
      // TODO: Determine dependency values based on filter config
      // For now, fetch with current filter values as potential dependencies
      fetchOptions(filter.id, filterValues);
    }
  }, [queryFilters, fetchOptions, filterValues]);

  const getOptions = useCallback(
    (filterId: string): FilterOption[] => {
      const filter = filters.find((f) => f.id === filterId);

      // If filter has an optionsSource (query-backed), prefer dynamic options
      if (filter && isQueryBackedFilter(filter)) {
        const dynamicOptions = optionsState[filterId]?.options;
        // Return dynamic options if loaded, otherwise fall back to static options as placeholder
        if (dynamicOptions && dynamicOptions.length > 0) {
          return dynamicOptions;
        }
        // Fall back to static options while loading
        return filter.options ?? [];
      }

      // For static-only filters, return static options
      if (filter?.options) {
        return filter.options;
      }

      // Return dynamic options as fallback
      return optionsState[filterId]?.options ?? [];
    },
    [filters, optionsState]
  );

  const isLoading = useCallback(
    (filterId: string): boolean => optionsState[filterId]?.loading ?? false,
    [optionsState]
  );

  const getError = useCallback(
    (filterId: string): ReportingApiError | null =>
      optionsState[filterId]?.error ?? null,
    [optionsState]
  );

  return {
    optionsState,
    fetchOptions,
    getOptions,
    isLoading,
    getError
  };
}

// ============================================================================
// useGenerateReport Hook
// ============================================================================

/**
 * Report run status for async operations.
 */
export type RunStatus = 'idle' | 'queued' | 'running' | 'succeeded' | 'failed';

/**
 * Result from useGenerateReport hook.
 */
export interface UseGenerateReportResult {
  /** Generate JSON report data (async with polling) */
  generateJson: (
    filters: FilterValues,
    options?: { page?: number; pageSize?: number }
  ) => Promise<ReportData | null>;

  /** Generate HTML preview (async with polling) */
  generateHtml: (
    filters: FilterValues,
    options?: { page?: number; pageSize?: number }
  ) => Promise<string | null>;

  /** Generate CSV export (async with polling) */
  generateCsv: (
    filters: FilterValues,
    options?: { pageSize?: number }
  ) => Promise<ExportMetadata | null>;

  /** Generate XLSX export (async with polling) */
  generateXlsx: (
    filters: FilterValues,
    options?: { pageSize?: number }
  ) => Promise<ExportMetadata | null>;

  /** Generate PDF export (async with polling) */
  generatePdf: (
    filters: FilterValues,
    options?: { pageSize?: number }
  ) => Promise<ExportMetadata | null>;

  /** Download a file with authentication */
  downloadFile: (url: string, filename: string) => Promise<void>;

  /** Current loading state */
  loading: boolean;

  /** Current run status for async operations */
  status: RunStatus;

  /** Status message from the server */
  statusMessage: string | null;

  /** Last error */
  error: ReportingApiError | null;

  /** Last JSON result */
  jsonData: ReportData | null;

  /** Last HTML result */
  htmlData: string | null;

  /** Pagination data from HTML response headers */
  htmlPagination: HtmlPaginationData | null;

  /** Last export metadata (CSV, XLSX, or PDF) */
  exportData: ExportMetadata | null;

  /** @deprecated Use exportData instead */
  csvData: ExportMetadata | null;

  /** Clear all results */
  clear: () => void;
}

/**
 * Hook for generating reports in various formats using async execution mode.
 *
 * All generation methods use async execution with polling, which is more
 * robust for large reports that might timeout with synchronous requests.
 *
 * @param config - SDK client config
 * @param reportId - Report ID
 */
export function useGenerateReport(
  config: ReportingClientConfig,
  reportId: string | null
): UseGenerateReportResult {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<RunStatus>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<ReportingApiError | null>(null);
  const [jsonData, setJsonData] = useState<ReportData | null>(null);
  const [htmlData, setHtmlData] = useState<string | null>(null);
  const [
    htmlPagination,
    setHtmlPagination
  ] = useState<HtmlPaginationData | null>(null);
  const [exportData, setExportData] = useState<ExportMetadata | null>(null);

  const clientRef = useRef<ReportingClient | null>(null);

  // Create client once
  if (!clientRef.current) {
    clientRef.current = createReportingClient(config);
  }

  /**
   * Handle status updates during polling.
   */
  const handleStatusChange = useCallback((run: ReportRunMetadata) => {
    setStatus(run.status as RunStatus);
    setStatusMessage(run.statusMessage || null);
  }, []);

  const generateJson = useCallback(
    async (
      filters: FilterValues,
      options?: { page?: number; pageSize?: number }
    ): Promise<ReportData | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);
      setStatus('queued');
      setStatusMessage('Starting report generation...');

      try {
        const result = await clientRef.current!.generateReportAsyncAndWait(
          reportId,
          {
            filters: prepareFiltersForSubmission(filters),
            format: 'html',
            renderHtml: false,
            page: options?.page ?? 1,
            pageSize: options?.pageSize ?? 50
          },
          { onStatusChange: handleStatusChange }
        );

        // Result should be ReportData for JSON format
        if (typeof result !== 'string' && 'data' in result) {
          setJsonData(result as ReportData);
          setStatus('succeeded');
          return result as ReportData;
        }

        throw new ReportingApiError('Unexpected response format');
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(
                err instanceof Error ? err.message : 'Unknown error'
              );
        setError(apiError);
        setStatus('failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId, handleStatusChange]
  );

  const generateHtml = useCallback(
    async (
      filters: FilterValues,
      options?: { page?: number; pageSize?: number }
    ): Promise<string | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);
      setStatus('queued');
      setStatusMessage('Starting report generation...');

      try {
        const result = await clientRef.current!.generateReportAsyncAndWait(
          reportId,
          {
            filters: prepareFiltersForSubmission(filters),
            format: 'html',
            renderHtml: true,
            page: options?.page ?? 1,
            pageSize: options?.pageSize ?? 50
          },
          { onStatusChange: handleStatusChange }
        );

        // Result is now HtmlReportResult with html and pagination
        const htmlResult = (result as unknown) as HtmlReportResult;
        if (htmlResult && typeof htmlResult.html === 'string') {
          setHtmlData(htmlResult.html);
          setHtmlPagination(htmlResult.pagination);
          setStatus('succeeded');
          return htmlResult.html;
        }

        // Fallback for legacy string response (shouldn't happen normally)
        if (typeof result === 'string') {
          setHtmlData(result);
          setHtmlPagination(null);
          setStatus('succeeded');
          return result;
        }

        throw new ReportingApiError('Unexpected response format');
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(
                err instanceof Error ? err.message : 'Unknown error'
              );
        setError(apiError);
        setStatus('failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId, handleStatusChange]
  );

  const generateCsv = useCallback(
    async (
      filters: FilterValues,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);
      setStatus('queued');
      setStatusMessage('Starting CSV export...');

      try {
        const result = await clientRef.current!.generateReportAsyncAndWait(
          reportId,
          {
            filters: prepareFiltersForSubmission(filters),
            format: 'csv',
            page: 1,
            pageSize: options?.pageSize ?? 10000
          },
          { onStatusChange: handleStatusChange }
        );

        // Result should be ReportRunMetadata with downloadUrl for exports
        if (typeof result !== 'string' && 'downloadUrl' in result) {
          const exportResult = result as ExportMetadata;
          setExportData(exportResult);
          setStatus('succeeded');
          return exportResult;
        }

        throw new ReportingApiError('Missing download URL in export response');
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(
                err instanceof Error ? err.message : 'Unknown error'
              );
        setError(apiError);
        setStatus('failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId, handleStatusChange]
  );

  const generateXlsx = useCallback(
    async (
      filters: FilterValues,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);
      setStatus('queued');
      setStatusMessage('Starting Excel export...');

      try {
        const result = await clientRef.current!.generateReportAsyncAndWait(
          reportId,
          {
            filters: prepareFiltersForSubmission(filters),
            format: 'xlsx',
            page: 1,
            pageSize: options?.pageSize ?? 10000
          },
          { onStatusChange: handleStatusChange }
        );

        // Result should be ReportRunMetadata with downloadUrl for exports
        if (typeof result !== 'string' && 'downloadUrl' in result) {
          const exportResult = result as ExportMetadata;
          setExportData(exportResult);
          setStatus('succeeded');
          return exportResult;
        }

        throw new ReportingApiError('Missing download URL in export response');
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(
                err instanceof Error ? err.message : 'Unknown error'
              );
        setError(apiError);
        setStatus('failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId, handleStatusChange]
  );

  const generatePdf = useCallback(
    async (
      filters: FilterValues,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata | null> => {
      if (!reportId) return null;

      setLoading(true);
      setError(null);
      setStatus('queued');
      setStatusMessage('Starting PDF export...');

      try {
        const result = await clientRef.current!.generateReportAsyncAndWait(
          reportId,
          {
            filters: prepareFiltersForSubmission(filters),
            format: 'pdf',
            page: 1,
            pageSize: options?.pageSize ?? 10000
          },
          { onStatusChange: handleStatusChange }
        );

        // Result should be ReportRunMetadata with downloadUrl for exports
        if (typeof result !== 'string' && 'downloadUrl' in result) {
          const exportResult = result as ExportMetadata;
          setExportData(exportResult);
          setStatus('succeeded');
          return exportResult;
        }

        throw new ReportingApiError('Missing download URL in export response');
      } catch (err) {
        const apiError =
          err instanceof ReportingApiError
            ? err
            : new ReportingApiError(
                err instanceof Error ? err.message : 'Unknown error'
              );
        setError(apiError);
        setStatus('failed');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reportId, handleStatusChange]
  );

  const clear = useCallback(() => {
    setJsonData(null);
    setHtmlData(null);
    setHtmlPagination(null);
    setStatus('idle');
    setStatusMessage(null);
    setExportData(null);
    setError(null);
  }, []);

  // Clear data when report changes
  useEffect(() => {
    clear();
  }, [reportId, clear]);

  /**
   * Download a file with authentication.
   * Use this to download export files that require auth headers.
   */
  const downloadFile = useCallback(
    async (url: string, filename: string): Promise<void> => {
      if (!clientRef.current) return;
      await clientRef.current.downloadFile(url, filename);
    },
    []
  );

  return {
    generateJson,
    generateHtml,
    generateCsv,
    generateXlsx,
    generatePdf,
    downloadFile,
    loading,
    status,
    statusMessage,
    error,
    jsonData,
    htmlData,
    htmlPagination,
    exportData,
    csvData: exportData, // Deprecated alias for backwards compatibility
    clear
  };
}

// ============================================================================
// Convenience Token Provider
// ============================================================================

/**
 * Create an auth provider from a token or token getter function.
 *
 * @param tokenOrGetter - Either a static token string or a function that returns a token (sync or async)
 *
 * @example Static token
 * ```ts
 * createTokenAuthProvider('my-static-token')
 * ```
 *
 * @example Dynamic token with refresh
 * ```ts
 * createTokenAuthProvider(() => getLatestToken())
 * ```
 */
export function createTokenAuthProvider(
  tokenOrGetter: string | (() => string | Promise<string>)
): AuthProviderConfig {
  if (typeof tokenOrGetter === 'string') {
    // Static token
    return {
      getAccessToken: () => tokenOrGetter
    };
  }
  // Dynamic token getter
  return {
    getAccessToken: tokenOrGetter
  };
}
